import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Prefetch critical data immediately after login so pages feel instant.
 */
export function useAppPrefetch() {
  const { user } = useUser();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    queryClient.prefetchQuery({
      queryKey: ['competitions'],
      queryFn: async () => {
        const { data } = await supabase
          .from('competitions')
          .select('id, country, competition, name, description, entry_fee, currency_code, currency_symbol, max_participants, status, start_date, end_date, registration_deadline, created_at, updated_at, deadline_timezone, locked, country_restrictions, winner_count, payout_percentages, admin_fee_percentage, calculated_prizes, first_place_prize, payout_locked, duration, display_name')
          .order('created_at', { ascending: false });
        return data;
      },
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
    });

    queryClient.prefetchQuery({
      queryKey: ['user-profile', user.id],
      queryFn: async () => {
        // SECURITY DEFINER RPC — direct profiles SELECT is RLS-blocked.
        const { data } = await (supabase.rpc as any)('get_my_profile_state', {
          _clerk_user_id: user.id,
        });
        return data;
      },
      staleTime: 5 * 60 * 1000,
    });

    (async () => {
      try {
        const token = await (window as any)?.Clerk?.session?.getToken?.().catch(() => null);
        queryClient.prefetchQuery({
          queryKey: ['fpl-bootstrap'],
          queryFn: async () => {
            const { data } = await supabase.functions.invoke('fpl-bootstrap', {
              ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
            });
            return data;
          },
          staleTime: 5 * 60 * 1000,
          gcTime: 24 * 60 * 60 * 1000,
        });
      } catch (_) { /* prefetch is best-effort */ }

      try {
        queryClient.prefetchQuery({
          queryKey: ['fpl-manager-performance', 'self', null],
          queryFn: async () => {
            const token = await (window as any)?.Clerk?.session?.getToken?.().catch(() => null);
            const { data } = await supabase.functions.invoke('fpl-manager-performance', {
              ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
            });
            return data;
          },
          staleTime: 10 * 60 * 1000,
          gcTime: 24 * 60 * 60 * 1000,
        });
      } catch (_) { /* prefetch is best-effort */ }

      try {
        queryClient.prefetchQuery({
          queryKey: ['fpl-suggestions'],
          queryFn: async () => {
            const token = await (window as any)?.Clerk?.session?.getToken?.().catch(() => null);
            const { data } = await supabase.functions.invoke('fpl-suggestions', {
              ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
            });
            return data;
          },
          staleTime: 15 * 60 * 1000,
          gcTime: 24 * 60 * 60 * 1000,
        });
      } catch (_) { /* prefetch is best-effort */ }
    })();
  }, [user?.id]);
}
